import React, { memo, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from '@mui/material';
import {
  Edit,
  Delete,
  Computer,
  LocationOn,
  People,
  Description,
  GroupAdd,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { canEdit, canDelete } from '../../utils/permissionUtils';
import { THEME_COLORS, BREAKPOINTS } from '../../constants';

/**
 * Componente de tabla de dispositivos para vista de escritorio
 */
const DevicesTable = memo(({
  devices,
  onEdit,
  onDelete,
  onConfigureTemplates,
  onManageContacts,
  canManage,
  currentUser,
  showActions = true,
  // Props de paginación
  pagination = null,
  onPageChange = null,
  onPageSizeChange = null,
}) => {
  const isTablet = useMediaQuery(`(max-width:${BREAKPOINTS.TABLET}px)`);
  
  // Calcular permisos una vez
  const permissions = useMemo(() => ({
    canEditDevices: canEdit(currentUser, 'devices'),
    canDeleteDevices: canDelete(currentUser, 'devices'),
  }), [currentUser]);



  // Componente de controles de paginación
  const PaginationControls = () => {
    if (!pagination) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 2,
          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
        }}
      >
        {/* Información de registros */}
        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
          Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} dispositivos
        </Typography>

        {/* Controles de navegación */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Selector de elementos por página */}
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={pagination.limit}
              onChange={(e) => onPageSizeChange?.(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                },
                '& .MuiSelect-select': {
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>

          {/* Botones de navegación */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!pagination.hasPrev}
              onClick={() => onPageChange?.(pagination.page - 1)}
              startIcon={<NavigateBeforeIcon />}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                borderColor: THEME_COLORS.BORDER_LIGHT,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: THEME_COLORS.TEXT_PRIMARY,
                  color: THEME_COLORS.TEXT_PRIMARY,
                },
                '&:disabled': {
                  color: THEME_COLORS.TEXT_MUTED,
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                },
              }}
            >
              Anterior
            </Button>

            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, px: 2 }}>
              Página {pagination.page} de {pagination.pages || 1}
            </Typography>

            <Button
              variant="outlined"
              size="small"
              disabled={!pagination.hasNext}
              onClick={() => onPageChange?.(pagination.page + 1)}
              endIcon={<NavigateNextIcon />}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                borderColor: THEME_COLORS.BORDER_LIGHT,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: THEME_COLORS.TEXT_PRIMARY,
                  color: THEME_COLORS.TEXT_PRIMARY,
                },
                '&:disabled': {
                  color: THEME_COLORS.TEXT_MUTED,
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                },
              }}
            >
              Siguiente
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  // Componente de fila vacía
  const EmptyTableRow = memo(() => (
    <TableRow>
      <TableCell colSpan={showActions ? 4 : 3} sx={{ textAlign: 'center', padding: 4 }}>
        <Typography variant="body1" sx={{ color: THEME_COLORS.TEXT_MUTED }}>
          No se encontraron dispositivos que coincidan con la búsqueda
        </Typography>
        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, mt: 1 }}>
          Intenta con otro término de búsqueda o añade un nuevo dispositivo.
        </Typography>
      </TableCell>
    </TableRow>
  ));

  // Componente de header de tabla
  const TableHeader = memo(() => (
    <TableHead>
      <TableRow sx={{ backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT }}>
        <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '0.875rem' }}>
          Dispositivo
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '0.875rem' }}>
          Ubicación
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '0.875rem' }}>
          Configuración
        </TableCell>
        {showActions && (
          <TableCell sx={{ 
            fontWeight: 600, 
            color: THEME_COLORS.TEXT_PRIMARY, 
            textAlign: 'center',
            fontSize: '0.875rem',
            minWidth: 200,
          }}>
            Acciones
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  ));

  // Componente de fila de dispositivo
  const DeviceRow = memo(({ device }) => {

    return (
      <React.Fragment>
        <TableRow 
          sx={{ 
            '&:hover': { 
              backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
            },
            transition: 'background-color 0.2s ease-in-out',
          }}
        >
          {/* Dispositivo */}
          <TableCell>
            <Box>
              <Typography
                variant="body2"
                sx={{ 
                  fontWeight: 500, 
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                }}
              >
                {device.nombre}
              </Typography>
              <Typography
                variant="caption"
                sx={{ 
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.75rem',
                }}
              >
                ID: {device.device_id.slice(0, 8)}...
              </Typography>
            </Box>
          </TableCell>

          {/* Ubicación */}
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationOn 
                sx={{ 
                  fontSize: 16, 
                  color: THEME_COLORS.TEXT_SECONDARY, 
                  marginRight: 1 
                }} 
              />
              <Typography
                variant="body2"
                sx={{ 
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                }}
              >
                {device.ubicacion_texto || 'Sin ubicación'}
              </Typography>
            </Box>
          </TableCell>

          {/* Configuración */}
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={<People />}
                label={`${device.contact_count || 0} contactos`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.75rem',
                }}
              />
              <Chip
                icon={<Description />}
                label={`${device.template_count || 0} plantillas`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
          </TableCell>

          {/* Acciones */}
          {showActions && (
            <TableCell sx={{ textAlign: 'center', minWidth: 200 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                {/* Gestionar Contactos */}
                <Tooltip title="Gestionar Contactos" arrow>
                  <IconButton
                    size="small"
                    sx={{
                      color: THEME_COLORS.TEXT_SECONDARY,
                      '&:hover': {
                        backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                        color: THEME_COLORS.TEXT_PRIMARY,
                      },
                    }}
                    onClick={() => onManageContacts(device)}
                  >
                    <GroupAdd fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Configurar Plantillas */}
                <Tooltip title="Configurar Plantillas" arrow>
                  <IconButton
                    size="small"
                    sx={{
                      color: THEME_COLORS.TEXT_SECONDARY,
                      '&:hover': {
                        backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                        color: THEME_COLORS.TEXT_PRIMARY,
                      },
                    }}
                    onClick={() => onConfigureTemplates(device)}
                  >
                    <Description fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Editar */}
                {permissions.canEditDevices && (
                  <Tooltip title="Editar Dispositivo" arrow>
                    <IconButton 
                      size="small" 
                      sx={{ 
                        color: THEME_COLORS.TEXT_SECONDARY,
                        '&:hover': {
                          backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                          color: THEME_COLORS.TEXT_PRIMARY,
                        },
                      }}
                      onClick={() => onEdit(device)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Eliminar */}
                {permissions.canDeleteDevices && (
                  <Tooltip title="Eliminar Dispositivo" arrow>
                    <IconButton 
                      size="small" 
                      sx={{ 
                        color: THEME_COLORS.TEXT_SECONDARY,
                        '&:hover': {
                          backgroundColor: '#ffebee',
                          color: '#d32f2f',
                        },
                      }}
                      onClick={() => onDelete(device)}
                    >
                      <Delete fontSize="small" />
                  </IconButton>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
          )}
        </TableRow>
      </React.Fragment>
    );
  });

  DeviceRow.displayName = 'DeviceRow';

  if (devices.length === 0) {
    return (
      <Box>
        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: '16px',
            border: `1px solid ${THEME_COLORS.BORDER}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          }}
        >
          <Table>
            <TableHeader />
            <TableBody>
              <EmptyTableRow />
            </TableBody>
          </Table>
        </TableContainer>
        <PaginationControls />
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: '16px',
          border: `1px solid ${THEME_COLORS.BORDER}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <Table>
          <TableHeader />
          <TableBody>
            {devices.map((device) => (
              <DeviceRow key={device.device_id} device={device} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationControls />
    </Box>
  );
});

DevicesTable.displayName = 'DevicesTable';

export default DevicesTable; 